import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Menu, X, ChevronDown, Zap, Battery, Gauge, Building2, Car, User, Briefcase, Globe, LogIn, Image, BookOpen, Smartphone, MapPin, Info } from 'lucide-react';

// Logo horizontale për ekrane të mëdha
const LOGO_HORIZONTAL = "/images/albmove-logo-horizontal.png";
// Logo ikona për ekrane të vogla/mesme  
const LOGO_ICON = "/images/albmove-logo-icon.png";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isStationsOpen, setIsStationsOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  // Separate state for mobile accordion (avoid conflict with desktop hover state)
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Detect if we're on English pages
  const isEnglish = location.pathname.startsWith('/en');
  const basePath = isEnglish ? '/en' : '';
  
  const serviceLinks = isEnglish ? [
    { href: '/en/services/ac-dc', label: 'AC/DC Charging', icon: Battery, desc: 'Normal and fast charging' },
    { href: '/en/services/fast-charging', label: 'Fast Charging', icon: Zap, desc: 'DC fast charging' },
    { href: '/en/services/ultra-fast', label: 'Ultra-Fast Charging', icon: Gauge, desc: 'Up to 240 kW' },
    { href: '/en/services/business', label: 'Business Solutions', icon: Building2, desc: 'Corporate packages' },
    { href: '/en/services/fleet', label: 'Fleet Management', icon: Car, desc: 'EV fleet management' },
  ] : [
    { href: '/services/ac-dc', label: 'Karikim AC/DC', icon: Battery, desc: 'Karikim normal dhe i shpejtë' },
    { href: '/services/fast-charging', label: 'Fast Charging', icon: Zap, desc: 'Karikim i shpejtë DC' },
    { href: '/services/ultra-fast', label: 'Ultra-Fast Charging', icon: Gauge, desc: 'Mbi 240 kW' },
    { href: '/services/business', label: 'Zgjidhje për Biznese', icon: Building2, desc: 'Paketa për kompani' },
    { href: '/services/fleet', label: 'Menaxhim Flotash', icon: Car, desc: 'Menaxhim i flotës elektrike' },
  ];
  
  const stationLinks = isEnglish ? [
    { href: '/en/stations', label: 'Charging Stations', icon: MapPin, desc: 'Find our stations' },
    { href: '/en/gallery', label: 'Gallery', icon: Image, desc: 'Photos of our stations' },
  ] : [
    { href: '/stations', label: 'Stacionet e Karikimit', icon: MapPin, desc: 'Gjeni stacionet tona' },
    { href: '/gallery', label: 'Galeria', icon: Image, desc: 'Foto nga stacionet tona' },
  ];
  
  const aboutLinks = isEnglish ? [
    { href: '/en/about', label: 'About AlbMove', icon: Info, desc: 'Our story and mission' },
    { href: '/en/blog', label: 'Blog & News', icon: BookOpen, desc: 'Latest updates' },
  ] : [
    { href: '/about', label: 'Rreth AlbMove', icon: Info, desc: 'Historia dhe misioni ynë' },
    { href: '/blog', label: 'Blog & Lajme', icon: BookOpen, desc: 'Lajmet më të fundit' },
  ];
  
  const navLinks = isEnglish ? [
    { href: '/en', label: 'Home' },
    { href: '/en/individuals', label: 'Individuals', icon: User },
    { href: '/en/business', label: 'Business', icon: Briefcase },
    { href: '/en/app', label: 'AlbMove App', icon: Smartphone },
    { href: '/en/contact', label: 'Contact' },
  ] : [
    { href: '/', label: 'Kryefaqja' },
    { href: '/individuals', label: 'Individë', icon: User },
    { href: '/business', label: 'Biznese', icon: Briefcase },
    { href: '/app', label: 'Aplikacioni AlbMove', icon: Smartphone },
    { href: '/contact', label: 'Kontakti' },
  ];
  
  const switchLanguage = (toLang) => {
    const currentPath = location.pathname;
    let newPath;
    
    if (toLang === 'en') {
      // Switch to English
      if (currentPath === '/') {
        newPath = '/en';
      } else if (!currentPath.startsWith('/en')) {
        newPath = '/en' + currentPath;
      } else {
        newPath = currentPath;
      }
    } else {
      // Switch to Albanian
      if (currentPath === '/en') {
        newPath = '/';
      } else if (currentPath.startsWith('/en/')) {
        newPath = currentPath.replace('/en', '');
      } else {
        newPath = currentPath;
      }
    }
    
    navigate(newPath);
    setIsLangOpen(false);
  };
  
  const isActive = (path) => location.pathname === path;
  const isServicesActive = () => location.pathname.includes('/services');
  
  return (
    <header 
      data-testid="main-header"
      className="fixed top-0 left-0 right-0 z-50 nav-blur bg-white/95 lg:border-b lg:border-slate-200/50"
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center group flex-shrink-0"
            data-testid="logo-link"
          >
            {/* Logo horizontale - shfaqet vetëm në ekrane të mëdha (xl+) */}
            <img 
              src={LOGO_HORIZONTAL} 
              alt="AlbMove Logo" 
              className="hidden xl:block h-10 w-auto group-hover:scale-105 transition-transform"
            />
            {/* Logo ikona - shfaqet në ekrane të vogla dhe mesme */}
            <img 
              src={LOGO_ICON} 
              alt="AlbMove Logo" 
              className="xl:hidden h-10 w-10 object-contain group-hover:scale-105 transition-transform"
            />
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {/* Services Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsServicesOpen(true)}
              onMouseLeave={() => setIsServicesOpen(false)}
            >
              <button
                data-testid="nav-services-dropdown"
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-1 ${
                  isServicesActive()
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-600 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                {isEnglish ? 'Services' : 'Shërbimet'}
                <ChevronDown className={`w-4 h-4 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              {isServicesOpen && (
                <div 
                  data-testid="services-dropdown-menu"
                  className="absolute top-full left-0 pt-2 w-72"
                >
                  <div className="bg-white rounded-2xl shadow-xl border border-slate-100 py-3 animate-fade-up">
                    {serviceLinks.map((link) => (
                      <Link
                        key={link.href}
                        to={link.href}
                        data-testid={`dropdown-${link.href.split('/').pop()}`}
                        className="flex items-start gap-3 px-4 py-3 hover:bg-blue-50 transition-colors group"
                      >
                        <div className="w-10 h-10 rounded-xl bg-blue-100 group-hover:bg-blue-200 flex items-center justify-center flex-shrink-0 transition-colors">
                          <link.icon className="w-5 h-5 text-blue-600" />
                        </div>
                      <div>
                        <p className="font-semibold text-slate-900 text-sm">{link.label}</p>
                        <p className="text-xs text-slate-500">{link.desc}</p>
                      </div>
                    </Link>
                  ))}
                  <div className="border-t border-slate-100 mt-2 pt-2 px-4">
                    <Link
                      to={isEnglish ? '/en/services' : '/services'}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      {isEnglish ? 'View all services →' : 'Shiko të gjitha shërbimet →'}
                    </Link>
                  </div>
                  </div>
                </div>
              )}
            </div>
            
            <Link
              to={isEnglish ? '/en/individuals' : '/individuals'}
              data-testid="nav-link-individuals"
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-1.5 ${
                isActive(isEnglish ? '/en/individuals' : '/individuals')
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-600 hover:text-blue-600 hover:bg-blue-50'
              }`}
            >
              <User className="w-4 h-4" />
              {isEnglish ? 'Individuals' : 'Individë'}
            </Link>
            
            <Link
              to={isEnglish ? '/en/business' : '/business'}
              data-testid="nav-link-business"
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-1.5 ${
                isActive(isEnglish ? '/en/business' : '/business')
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-600 hover:text-blue-600 hover:bg-blue-50'
              }`}
            >
              <Briefcase className="w-4 h-4" />
              {isEnglish ? 'Business' : 'Biznese'}
            </Link>
            
            {/* Stations Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsStationsOpen(true)}
              onMouseLeave={() => setIsStationsOpen(false)}
            >
              <button
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-1 ${
                  location.pathname.includes('/stations') || location.pathname.includes('/gallery')
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-600 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                {isEnglish ? 'Stations' : 'Stacionet'}
                <ChevronDown className={`w-4 h-4 transition-transform ${isStationsOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isStationsOpen && (
                <div className="absolute top-full left-0 mt-2 bg-white rounded-2xl shadow-xl border border-slate-200/50 py-3 min-w-[240px] z-50">
                  {stationLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                        <link.icon className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900 text-sm">{link.label}</p>
                        <p className="text-xs text-slate-500">{link.desc}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
            
            {/* About Us Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsAboutOpen(true)}
              onMouseLeave={() => setIsAboutOpen(false)}
            >
              <button
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-1 ${
                  location.pathname.includes('/about') || location.pathname.includes('/blog')
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-600 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                {isEnglish ? 'About Us' : 'Rreth Nesh'}
                <ChevronDown className={`w-4 h-4 transition-transform ${isAboutOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isAboutOpen && (
                <div className="absolute top-full left-0 mt-2 bg-white rounded-2xl shadow-xl border border-slate-200/50 py-3 min-w-[240px] z-50">
                  {aboutLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                        <link.icon className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900 text-sm">{link.label}</p>
                        <p className="text-xs text-slate-500">{link.desc}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
            
            <Link
              to={isEnglish ? '/en/app' : '/app'}
              data-testid="nav-link-app"
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-1.5 ${
                isActive(isEnglish ? '/en/app' : '/app')
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-600 hover:text-blue-600 hover:bg-blue-50'
              }`}
            >
              <Smartphone className="w-4 h-4" />
              {isEnglish ? 'AlbMove App' : 'Aplikacioni'}
            </Link>
            
            <Link
              to={isEnglish ? '/en/contact' : '/contact'}
              data-testid="nav-link-contact"
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                isActive(isEnglish ? '/en/contact' : '/contact')
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-600 hover:text-blue-600 hover:bg-blue-50'
              }`}
            >
              {isEnglish ? 'Contact' : 'Kontakti'}
            </Link>
          </nav>
          
          {/* CTA Button + Language Switcher + Login */}
          <div className="hidden lg:flex items-center gap-3">
            {/* Language Switcher */}
            <div 
              className="relative"
              onMouseEnter={() => setIsLangOpen(true)}
              onMouseLeave={() => setIsLangOpen(false)}
            >
              <button
                data-testid="language-switcher"
                className="flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <Globe className="w-4 h-4" />
                {isEnglish ? 'EN' : 'SQ'}
                <ChevronDown className={`w-4 h-4 transition-transform ${isLangOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isLangOpen && (
                <div className="absolute top-full right-0 mt-1 bg-white rounded-xl shadow-lg border border-slate-200 py-2 min-w-[140px]">
                  <button
                    onClick={() => switchLanguage('sq')}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-3 ${!isEnglish ? 'text-blue-600 font-semibold' : 'text-slate-600'}`}
                  >
                    <img src="https://flagcdn.com/w40/al.png" alt="Shqip" className="w-5 h-auto rounded-sm" />
                    Shqip
                  </button>
                  <button
                    onClick={() => switchLanguage('en')}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-3 ${isEnglish ? 'text-blue-600 font-semibold' : 'text-slate-600'}`}
                  >
                    <img src="https://flagcdn.com/w40/gb.png" alt="English" className="w-5 h-auto rounded-sm" />
                    English
                  </button>
                </div>
              )}
            </div>
            
            <Button 
              asChild
              data-testid="header-login-button"
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6 font-semibold"
            >
              <a href="https://app.albmove.al" target="_blank" rel="noopener noreferrer">
                <LogIn className="w-4 h-4 mr-2" />
                Login
              </a>
            </Button>
          </div>
          
          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? 'Mbyll menunë' : 'Hap menunë'}
          >
            {isMenuOpen ? (
              <X className="w-6 h-6 text-slate-700" />
            ) : (
              <Menu className="w-6 h-6 text-slate-700" />
            )}
          </button>
        </div>
      </div>
      
      {/* Mobile Menu */}
      {isMenuOpen && (
        <div 
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-t border-slate-100 animate-fade-up max-h-[80vh] overflow-y-auto"
        >
          <nav className="container-custom py-4 flex flex-col gap-2">
            {/* Mobile Services Accordion */}
            <div className="border-b border-slate-100 pb-2">
              <button
                onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                data-testid="mobile-services-accordion"
                className="w-full px-4 py-3 rounded-xl text-base font-semibold text-slate-600 hover:bg-blue-50 flex items-center justify-between"
              >
                {isEnglish ? 'Services' : 'Shërbimet'}
                <ChevronDown className={`w-5 h-5 transition-transform ${isMobileServicesOpen ? 'rotate-180' : ''}`} />
              </button>
              {isMobileServicesOpen && (
                <div className="pl-4 mt-1 space-y-1" data-testid="mobile-services-submenu">
                  {serviceLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => { setIsMenuOpen(false); setIsMobileServicesOpen(false); }}
                      className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-slate-600 hover:bg-blue-50"
                    >
                      <link.icon className="w-4 h-4 text-blue-600" />
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            
            <Link
              to={isEnglish ? '/en/individuals' : '/individuals'}
              onClick={() => setIsMenuOpen(false)}
              className={`px-4 py-3 rounded-xl text-base font-semibold flex items-center gap-2 ${
                isActive(isEnglish ? '/en/individuals' : '/individuals') ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-blue-50'
              }`}
            >
              <User className="w-5 h-5" />
              {isEnglish ? 'Individuals' : 'Për Individë'}
            </Link>
            
            <Link
              to={isEnglish ? '/en/business' : '/business'}
              onClick={() => setIsMenuOpen(false)}
              className={`px-4 py-3 rounded-xl text-base font-semibold flex items-center gap-2 ${
                isActive(isEnglish ? '/en/business' : '/business') ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-blue-50'
              }`}
            >
              <Briefcase className="w-5 h-5" />
              {isEnglish ? 'Business' : 'Për Biznese'}
            </Link>
            
            <Link
              to={isEnglish ? '/en/stations' : '/stations'}
              onClick={() => setIsMenuOpen(false)}
              className={`px-4 py-3 rounded-xl text-base font-semibold ${
                isActive(isEnglish ? '/en/stations' : '/stations') ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-blue-50'
              }`}
            >
              {isEnglish ? 'Stations' : 'Stacionet'}
            </Link>
            
            <Link
              to={isEnglish ? '/en/about' : '/about'}
              onClick={() => setIsMenuOpen(false)}
              className={`px-4 py-3 rounded-xl text-base font-semibold ${
                isActive(isEnglish ? '/en/about' : '/about') ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-blue-50'
              }`}
            >
              {isEnglish ? 'About Us' : 'Rreth Nesh'}
            </Link>
            
            <Link
              to={isEnglish ? '/en/gallery' : '/gallery'}
              onClick={() => setIsMenuOpen(false)}
              className={`px-4 py-3 rounded-xl text-base font-semibold flex items-center gap-2 ${
                isActive(isEnglish ? '/en/gallery' : '/gallery') ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-blue-50'
              }`}
            >
              <Image className="w-5 h-5" />
              {isEnglish ? 'Gallery' : 'Galeria'}
            </Link>
            
            <Link
              to={isEnglish ? '/en/blog' : '/blog'}
              onClick={() => setIsMenuOpen(false)}
              className={`px-4 py-3 rounded-xl text-base font-semibold flex items-center gap-2 ${
                isActive(isEnglish ? '/en/blog' : '/blog') ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-blue-50'
              }`}
            >
              <BookOpen className="w-5 h-5" />
              Blog
            </Link>
            
            <Link
              to={isEnglish ? '/en/contact' : '/contact'}
              onClick={() => setIsMenuOpen(false)}
              className={`px-4 py-3 rounded-xl text-base font-semibold ${
                isActive(isEnglish ? '/en/contact' : '/contact') ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-blue-50'
              }`}
            >
              {isEnglish ? 'Contact' : 'Kontakti'}
            </Link>
            
            {/* Mobile Language Switcher */}
            <div className="border-t border-slate-100 pt-4 mt-2">
              <p className="text-xs text-slate-500 mb-2 px-4">{isEnglish ? 'Language' : 'Gjuha'}</p>
              <div className="flex gap-2 px-4">
                <button
                  onClick={() => { switchLanguage('sq'); setIsMenuOpen(false); }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${!isEnglish ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'}`}
                >
                  <img src="https://flagcdn.com/w40/al.png" alt="Shqip" className="w-5 h-auto rounded-sm" />
                  Shqip
                </button>
                <button
                  onClick={() => { switchLanguage('en'); setIsMenuOpen(false); }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${isEnglish ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'}`}
                >
                  <img src="https://flagcdn.com/w40/gb.png" alt="English" className="w-5 h-auto rounded-sm" />
                  English
                </button>
              </div>
            </div>
            
            <Button 
              asChild
              className="mt-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold"
            >
              <a href="https://app.albmove.al" target="_blank" rel="noopener noreferrer">
                <LogIn className="w-4 h-4 mr-2" />
                Login
              </a>
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
